/** Shared payload parsing helpers for FCM, socket, and service worker push handling. */

const parseJsonSafe = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
};

/** Novu / backend sometimes stringifies body with Python-style single quotes. */
const parsePythonStyleObjectString = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return null;
  }

  try {
    const jsonLike = trimmed.replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, '"$1"');
    const parsed = JSON.parse(jsonLike);
    return isPlainObject(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
};

export const isPlainObject = (value) =>
  value != null && typeof value === 'object' && !Array.isArray(value);

export const parseObjectSafe = (value) => {
  if (isPlainObject(value)) {
    return value;
  }
  return parseJsonSafe(value) || parsePythonStyleObjectString(value);
};

export const normalizePushText = (value) => {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
};

export const readPushTypeKeys = (data = {}) => ({
  type: normalizePushText(data?.type).toLowerCase(),
  eventType: normalizePushText(data?.eventType || data?.notificationType).toLowerCase(),
});

/**
 * Normalizes Novu / backend FCM data payloads where fields like eventType and amount
 * live inside stringified body/meta objects.
 */
const mergeRootPushFields = (payload = {}) => {
  const data = payload?.data || {};
  const rootFields = {};

  for (const key of [
    'body',
    'meta',
    'payload',
    'message',
    'title',
    'subject',
    'type',
    'eventType',
    'notificationType',
  ]) {
    const rootValue = payload?.[key];
    if (rootValue == null || rootValue === '') {
      continue;
    }
    if (data?.[key] == null || data?.[key] === '') {
      rootFields[key] = rootValue;
    }
  }

  return { ...data, ...rootFields };
};

export const flattenPushDataPayload = (payload = {}) => {
  const data = mergeRootPushFields(payload);
  const parsedBody = parseObjectSafe(data.body);
  const parsedMeta = parseObjectSafe(data.meta);
  const parsedPayload = parseObjectSafe(data.payload);
  const topLevelPayload = parseObjectSafe(payload?.payload);
  const topLevelMeta = parseObjectSafe(payload?.meta);

  const nestedBodyMeta = isPlainObject(parsedBody?.meta)
    ? parsedBody.meta
    : parseObjectSafe(parsedBody?.meta);

  const bodyWithoutMeta = isPlainObject(parsedBody)
    ? Object.fromEntries(Object.entries(parsedBody).filter(([key]) => key !== 'meta'))
    : {};

  const dataWithoutStructuredFields = isPlainObject(data)
    ? Object.fromEntries(
        Object.entries(data).filter(([key]) => !['body', 'meta', 'payload'].includes(key))
      )
    : {};

  return {
    ...(isPlainObject(topLevelPayload) ? topLevelPayload : {}),
    ...(isPlainObject(topLevelMeta) ? topLevelMeta : {}),
    ...dataWithoutStructuredFields,
    ...(isPlainObject(bodyWithoutMeta) ? bodyWithoutMeta : {}),
    ...(isPlainObject(nestedBodyMeta) ? nestedBodyMeta : {}),
    ...(isPlainObject(parsedMeta) ? parsedMeta : {}),
    ...(isPlainObject(parsedPayload) ? parsedPayload : {}),
  };
};

/**
 * Socket `notification:new` envelopes expose fields at the root as well as in nested data/meta.
 * Reuses FCM flattening after wrapping the envelope in an FCM-compatible shape.
 */
export const flattenSocketNotificationPayload = (payload = {}) => {
  const rootData =
    payload && typeof payload === 'object'
      ? Object.fromEntries(
          Object.entries(payload).filter(
            ([key]) => !['data', 'meta', 'payload'].includes(key)
          )
        )
      : {};

  const meta =
    parseObjectSafe(payload.meta) ||
    (payload.meta && typeof payload.meta === 'object' ? payload.meta : {}) ||
    {};

  const data =
    parseObjectSafe(payload.data) ||
    (payload.data && typeof payload.data === 'object' ? payload.data : {}) ||
    {};

  return flattenPushDataPayload({
    ...rootData,
    meta,
    payload: payload.payload,
    data: {
      ...data,
      body: data.body ?? payload.body,
      meta: data.meta ?? meta,
      payload: data.payload ?? payload.payload,
    },
  });
};

const isStructuredPushPayloadJson = (value) => {
  const parsed = parseObjectSafe(value);
  if (!isPlainObject(parsed)) {
    return false;
  }

  return Boolean(parsed.schemaVersion || parsed.type || parsed.message);
};

const readStructuredPushMessage = (value) => {
  if (!isStructuredPushPayloadJson(value)) {
    return '';
  }

  const parsed = parseObjectSafe(value);
  return normalizePushText(parsed?.message);
};

const looksLikeSerializedStructuredPushBody = (value) => {
  const text = normalizePushText(value);
  if (!text.startsWith('{')) {
    return false;
  }

  return (
    text.includes('schemaVersion') ||
    text.includes('creator_trending') ||
    text.includes('notificationId')
  );
};

const readPlainPushBody = (value) => {
  if (isStructuredPushPayloadJson(value)) {
    return '';
  }

  if (looksLikeSerializedStructuredPushBody(value)) {
    return readStructuredPushMessage(value) || '';
  }

  return normalizePushText(value);
};

export const resolvePushTitle = (payload, data) =>
  normalizePushText(payload?.notification?.title) ||
  normalizePushText(payload?.title) ||
  normalizePushText(payload?.subject) ||
  normalizePushText(data?.title) ||
  normalizePushText(data?.subject) ||
  '';

export const resolvePushBody = (payload, data) =>
  normalizePushText(data?.message) ||
  normalizePushText(payload?.message) ||
  readStructuredPushMessage(data?.body) ||
  readStructuredPushMessage(payload?.body) ||
  readStructuredPushMessage(payload?.notification?.body) ||
  readPlainPushBody(payload?.notification?.body) ||
  readPlainPushBody(data?.body) ||
  readPlainPushBody(payload?.body) ||
  '';
